import React, { useState, useRef, KeyboardEvent } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  maxTags?: number;
  maxLength?: number;
  suggestions?: string[];
  allowDuplicates?: boolean;
  icon?: React.ReactNode;
  helperText?: string;
}

export const TagInput: React.FC<TagInputProps> = ({
  value = [],
  onChange,
  label,
  placeholder = 'Type and press Enter',
  required = false,
  disabled = false,
  error,
  className = '',
  maxTags = 50,
  maxLength = 100,
  suggestions = [],
  allowDuplicates = false,
  icon,
  helperText,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter suggestions based on input
  const filteredSuggestions = suggestions.filter(
    suggestion => 
      suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
      !value.includes(suggestion)
  );

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    
    if (!trimmedTag) return;
    
    if (trimmedTag.length > maxLength) {
      return;
    }
    
    if (!allowDuplicates && value.includes(trimmedTag)) {
      return;
    }
    
    if (value.length >= maxTags) {
      return;
    }
    
    onChange([...value, trimmedTag]);
    setInputValue('');
    setShowSuggestions(false);
    setFocusedSuggestionIndex(-1);
  };

  const removeTag = (index: number) => {
    if (disabled) return;
    
    const newTags = value.filter((_, i) => i !== index);
    onChange(newTags);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (focusedSuggestionIndex >= 0 && filteredSuggestions[focusedSuggestionIndex]) {
        addTag(filteredSuggestions[focusedSuggestionIndex]);
      } else {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value.length - 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedSuggestionIndex(prev => 
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setFocusedSuggestionIndex(-1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(value.length > 0 && filteredSuggestions.length > 0);
    setFocusedSuggestionIndex(-1);
  };

  const handleSuggestionClick = (suggestion: string) => {
    addTag(suggestion);
    inputRef.current?.focus();
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div
          className={`min-h-[42px] px-3 py-2 border rounded-md transition-colors ${
            disabled
              ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
              : error
              ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-500'
              : 'border-gray-300 focus-within:border-primary-500 focus-within:ring-primary-500'
          } focus-within:ring-1`}
          onClick={() => inputRef.current?.focus()}
        >
          <div className="flex flex-wrap gap-2 items-center">
            {icon && (
              <div className="text-gray-400">
                {icon}
              </div>
            )}
            
            {/* Tags */}
            {value.map((tag, index) => (
              <div
                key={index}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm ${
                  disabled
                    ? 'bg-gray-100 text-gray-500'
                    : 'bg-primary-100 text-primary-800'
                }`}
              >
                <span>{tag}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTag(index);
                    }}
                    className="hover:text-primary-900 transition-colors"
                    aria-label={`Remove ${tag}`}
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            
            {/* Input */}
            {!disabled && value.length < maxTags && (
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowSuggestions(inputValue.length > 0 && filteredSuggestions.length > 0)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder={value.length === 0 ? placeholder : ''}
                className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
                disabled={disabled}
              />
            )}
          </div>
        </div>
        
        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                  index === focusedSuggestionIndex ? 'bg-primary-50 text-primary-900' : ''
                }`}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Helper text or error */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      
      {helperText && !error && (
        <p className="text-xs text-gray-500">{helperText}</p>
      )}
      
      {/* Status text */}
      <div className="flex justify-between text-xs text-gray-500">
        <span>
          {value.length} item{value.length !== 1 ? 's' : ''}
          {maxTags && ` (max ${maxTags})`}
        </span>
        {!disabled && (
          <span>Press Enter to add</span>
        )}
      </div>
    </div>
  );
};